import React, { useState, useEffect, useCallback } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  ArrowLeft, Video, Clock, Timer, RefreshCw, ChevronUp, ChevronDown,
  ChevronsUpDown, ExternalLink, MicOff, VideoOff, PhoneOff,
  Lock, Unlock, Volume2, VolumeX, MoreHorizontal,
  UserX, UserCheck
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/clientWeb/components/ui/dialog"
import { toast } from "@/clientWeb/components/ui/toast-utils"

const AVATAR_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899", "#0ea5e9"]

interface LiveMeeting {
  id: string
  roomId: string
  title: string
  host: string
  hostEmail: string
  hostAvatar: string | null
  participants: number
  entryTime: number
  meetingId: string
  attendees: { name: string; email: string; isHost: boolean }[]
}

// No analytics API yet (see services/analytics.repo.ts), so no live meetings
// are known. This used to list three invented meetings ("Daily Standup" hosted
// by "John Doe"…) whose host controls only showed toasts.
const LIVE_MEETINGS: LiveMeeting[] = []

type SortKey = "title" | "host" | "participants" | "entryTime" | "duration"
type SortDir = "asc" | "desc"

function formatElapsed(ms: number): string {
  const totalSecs = Math.floor((Date.now() - ms) / 1000)
  const h = Math.floor(totalSecs / 3600)
  const m = Math.floor((totalSecs % 3600) / 60)
  const s = totalSecs % 60
  return `${String(h).padStart(2, "0")}h:${String(m).padStart(2, "0")}m:${String(s).padStart(2, "0")}s`
}

function formatStartTime(ms: number): string {
  return new Date(ms).toLocaleString("en-US", {
    month: "short", day: "2-digit", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  })
}

function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" | "lg" }) {
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length
  const sz = size === "sm" ? "w-7 h-7 text-[10px]" : size === "lg" ? "w-10 h-10 text-sm" : "w-8 h-8 text-xs"
  return (
    <div
      className={`${sz} rounded-full flex items-center justify-center font-bold text-white shrink-0`}
      style={{ background: AVATAR_COLORS[idx] }}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

function SortIcon({ field, sortKey, sortDir }: { field: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (sortKey !== field) return <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground/50" />
  return sortDir === "asc"
    ? <ChevronUp className="w-3.5 h-3.5 text-primary" />
    : <ChevronDown className="w-3.5 h-3.5 text-primary" />
}

type AlertAction =
  | { type: "end_meeting"; meetingId: string; title: string }
  | { type: "mute_all"; meetingId: string; title: string }
  | { type: "lock"; meetingId: string; title: string; locked: boolean }
  | { type: "remove_participant"; meetingId: string; participantName: string }
  | { type: "mute_participant"; meetingId: string; participantName: string; muted: boolean }
  | { type: "disable_video"; meetingId: string; participantName: string; videoOff: boolean }

export default function LiveMeetingsPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [sortKey, setSortKey] = useState<SortKey>("entryTime")
  const [sortDir, setSortDir] = useState<SortDir>("desc")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [, setNow] = useState(Date.now())
  const [alert, setAlert] = useState<AlertAction | null>(null)

  // Per-meeting local control state
  const [meetingStates, setMeetingStates] = useState<Record<string, {
    locked: boolean
    mutedParticipants: Record<string, boolean>
    videoOffParticipants: Record<string, boolean>
  }>>({})

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const getMeetingState = (meetingId: string) =>
    meetingStates[meetingId] ?? { locked: false, mutedParticipants: {}, videoOffParticipants: {} }

  const patchMeetingState = (meetingId: string, patch: Partial<ReturnType<typeof getMeetingState>>) =>
    setMeetingStates(prev => ({
      ...prev,
      [meetingId]: { ...getMeetingState(meetingId), ...patch }
    }))

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    setTimeout(() => setRefreshing(false), 1200)
  }, [])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortKey(key); setSortDir("asc") }
  }

  const confirmAction = () => {
    if (!alert) return
    const ms = getMeetingState(alert.meetingId)

    if (alert.type === "end_meeting") {
      toast.error({ title: `Meeting ended`, description: `"${alert.title}" has been ended.` })
    } else if (alert.type === "mute_all") {
      const mutedAll: Record<string, boolean> = {}
      LIVE_MEETINGS.find(m => m.id === alert.meetingId)?.attendees.forEach(a => {
        if (!a.isHost) mutedAll[a.name] = true
      })
      patchMeetingState(alert.meetingId, { mutedParticipants: { ...ms.mutedParticipants, ...mutedAll } })
      toast.info({ title: "All participants muted" })
    } else if (alert.type === "lock") {
      patchMeetingState(alert.meetingId, { locked: !ms.locked })
      toast.info({ title: ms.locked ? "Meeting unlocked" : "Meeting locked" })
    } else if (alert.type === "remove_participant") {
      toast.warning({ title: `${alert.participantName} removed` })
    } else if (alert.type === "mute_participant") {
      const next = { ...ms.mutedParticipants, [alert.participantName]: !alert.muted }
      patchMeetingState(alert.meetingId, { mutedParticipants: next })
      toast.info({ title: `${alert.participantName} ${alert.muted ? "unmuted" : "muted"}` })
    } else if (alert.type === "disable_video") {
      const next = { ...ms.videoOffParticipants, [alert.participantName]: !alert.videoOff }
      patchMeetingState(alert.meetingId, { videoOffParticipants: next })
      toast.info({ title: `Video ${alert.videoOff ? "enabled" : "disabled"} for ${alert.participantName}` })
    }
    setAlert(null)
  }

  const sorted = [...LIVE_MEETINGS].sort((a, b) => {
    let va: string | number = ""
    let vb: string | number = ""
    if (sortKey === "title") { va = a.title.toLowerCase(); vb = b.title.toLowerCase() }
    else if (sortKey === "host") { va = a.host.toLowerCase(); vb = b.host.toLowerCase() }
    else if (sortKey === "participants") { va = a.participants; vb = b.participants }
    else if (sortKey === "entryTime") { va = a.entryTime; vb = b.entryTime }
    else if (sortKey === "duration") { va = a.entryTime; vb = b.entryTime }
    if (va < vb) return sortDir === "asc" ? -1 : 1
    if (va > vb) return sortDir === "asc" ? 1 : -1
    return 0
  })

  const alertMeta = alert ? (() => {
    if (alert.type === "end_meeting") return {
      icon: <PhoneOff className="w-5 h-5 text-red-500" />,
      title: t("analytics.endMeeting"),
      desc: `${t("analytics.endMeetingConfirm")} "${alert.title}"? ${t("analytics.endMeetingDesc")}`,
      confirmLabel: t("analytics.endMeeting"), confirmClass: "bg-red-500 hover:bg-red-600 text-white"
    }
    if (alert.type === "mute_all") return {
      icon: <MicOff className="w-5 h-5 text-orange-500" />,
      title: t("analytics.muteAllParticipants"),
      desc: `${t("analytics.muteAllDesc1")} "${alert.title}". ${t("analytics.muteAllDesc2")}`,
      confirmLabel: t("analytics.muteAll"), confirmClass: "bg-orange-500 hover:bg-orange-600 text-white"
    }
    if (alert.type === "lock") return {
      icon: alert.locked ? <Unlock className="w-5 h-5 text-blue-500" /> : <Lock className="w-5 h-5 text-blue-500" />,
      title: alert.locked ? t("analytics.unlockMeeting") : t("analytics.lockMeeting"),
      desc: alert.locked
        ? t("analytics.unlockMeetingDesc")
        : t("analytics.lockMeetingDesc"),
      confirmLabel: alert.locked ? t("analytics.unlock") : t("analytics.lock"), confirmClass: "bg-blue-500 hover:bg-blue-600 text-white"
    }
    if (alert.type === "remove_participant") return {
      icon: <UserX className="w-5 h-5 text-red-500" />,
      title: t("analytics.removeParticipant"),
      desc: `${t("analytics.removeParticipantConfirm")} "${alert.participantName}"? ${t("analytics.removeParticipantDesc")}`,
      confirmLabel: t("analytics.remove"), confirmClass: "bg-red-500 hover:bg-red-600 text-white"
    }
    if (alert.type === "mute_participant") return {
      icon: alert.muted ? <Volume2 className="w-5 h-5 text-emerald-500" /> : <VolumeX className="w-5 h-5 text-orange-500" />,
      title: alert.muted ? `${t("analytics.unmute")} ${alert.participantName}` : `${t("analytics.mute")} ${alert.participantName}`,
      desc: alert.muted
        ? `${t("analytics.allowSpeakAgain")} ${alert.participantName}.`
        : `${alert.participantName}${t("analytics.microphoneMuted")}`,
      confirmLabel: alert.muted ? t("analytics.unmute") : t("analytics.mute"), confirmClass: "bg-orange-500 hover:bg-orange-600 text-white"
    }
    if (alert.type === "disable_video") return {
      icon: <VideoOff className="w-5 h-5 text-slate-500" />,
      title: alert.videoOff ? `${t("analytics.enableVideoFor")} ${alert.participantName}` : `${t("analytics.disableVideoFor")} ${alert.participantName}`,
      desc: alert.videoOff
        ? `${t("analytics.allowCameraOn")} ${alert.participantName}.`
        : `${t("analytics.turnOffCamera")} ${alert.participantName}.`,
      confirmLabel: alert.videoOff ? t("analytics.enableVideo") : t("analytics.disableVideo"), confirmClass: "bg-slate-600 hover:bg-slate-700 text-white"
    }
    return null
  })() : null

  return (
    <div className="flex p-4 flex-col flex-1 h-full overflow-hidden">
      {/* ── Alert Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={!!alert} onOpenChange={() => setAlert(null)}>
        <DialogContent className="max-w-sm">
          {alertMeta && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    {alertMeta.icon}
                  </div>
                  <DialogTitle>{alertMeta.title}</DialogTitle>
                </div>
                <DialogDescription>{alertMeta.desc}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => setAlert(null)}>{t("common.cancel")}</Button>
                <Button size="sm" className={alertMeta.confirmClass} onClick={confirmAction}>
                  {alertMeta.confirmLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="px-4 sm:px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <button onClick={() => navigate("/analytics")} className="hover:text-foreground text-primary font-semibold flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-3 h-3" /> {t("analytics.title")}
          </button>
          <span>/</span>
          <span className="text-foreground font-medium">{t("analytics.liveMeetings")}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              {t("analytics.liveMeetings")}
            </h1>
            <Badge variant="secondary" className="text-xs px-2.5">{LIVE_MEETINGS.length} {t("analytics.active")}</Badge>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {t("common.refresh")}
          </Button>
        </div>
        <div className="flex items-center gap-6 mt-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Video className="w-3.5 h-3.5" />
            <span><strong className="text-foreground">{LIVE_MEETINGS.length}</strong> {t("analytics.meetingsInProgress")}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Timer className="w-3.5 h-3.5" />
            <span><strong className="text-foreground">{LIVE_MEETINGS.reduce((s, m) => s + m.participants, 0)}</strong> {t("analytics.totalParticipants")}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            <span>{t("analytics.clickRowToManage")}</span>
          </div>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-x-auto overflow-y-auto rounded-xl border border-border bg-card">
        {LIVE_MEETINGS.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 py-20 text-center">
            <Video className="w-16 h-16 text-muted-foreground/20" />
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">{t("analytics.noLiveMeetings")}</h3>
              <p className="text-sm text-muted-foreground max-w-sm">{t("analytics.meetingsWillAppearHere")}</p>
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {([
                  { key: "title", label: t("analytics.meetingName") },
                  { key: "host", label: t("analytics.host") },
                  { key: "participants", label: t("common.participants") },
                  { key: "entryTime", label: t("analytics.startedAt") },
                  { key: "duration", label: t("analytics.duration") },
                ] as { key: SortKey; label: string }[]).map(col => (
                  <TableHead key={col.key} onClick={() => handleSort(col.key)} className="cursor-pointer select-none whitespace-nowrap">
                    <div className="flex items-center gap-1">{col.label}<SortIcon field={col.key} sortKey={sortKey} sortDir={sortDir} /></div>
                  </TableHead>
                ))}
                <TableHead className="w-24">{t("analytics.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((meeting, i) => {
                const ms = getMeetingState(meeting.id)
                const isExpanded = selectedId === meeting.id
                return (
                  <React.Fragment key={meeting.id}>
                    <motion.tr
                      key={meeting.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      onClick={() => setSelectedId(isExpanded ? null : meeting.id)}
                      className={`cursor-pointer hover:bg-muted/50 transition-colors border-b border-border ${isExpanded ? "bg-primary/5" : ""}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="relative flex h-2 w-2 shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                          </span>
                          <span className="font-medium text-foreground truncate max-w-[180px]">{meeting.title}</span>
                          {ms.locked && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar name={meeting.host} size="sm" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{meeting.host}</p>
                            <p className="text-[11px] text-muted-foreground truncate">{meeting.hostEmail}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex -space-x-1.5">
                            {meeting.attendees.slice(0, 3).map((a, idx) => (
                              <div key={idx} className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 border-card"
                                style={{ background: AVATAR_COLORS[(a.name.charCodeAt(0)) % AVATAR_COLORS.length] }} title={a.name}>
                                {a.name.charAt(0)}
                              </div>
                            ))}
                            {meeting.participants > 3 && (
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold bg-muted text-muted-foreground border-2 border-card">
                                +{meeting.participants - 3}
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-medium">{meeting.participants}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">{formatStartTime(meeting.entryTime)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm text-foreground">{formatElapsed(meeting.entryTime)}</span>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button size="sm" className="h-7 text-xs gap-1 px-2.5">
                            <ExternalLink className="w-3 h-3" /> {t("common.join")}
                          </Button>
                          <button onClick={() => setSelectedId(isExpanded ? null : meeting.id)}
                            className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors">
                            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </div>
                      </TableCell>
                    </motion.tr>

                    {/* ── Expanded host-controls panel ───────────────────── */}
                    <AnimatePresence>
                      {isExpanded && (
                        <tr key={`${meeting.id}-ctrl`}>
                          <td colSpan={6} className="p-0">
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 py-5 bg-muted/20 border-b border-border space-y-5">

                                {/* Meeting-level controls */}
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("analytics.meetingControls")}</p>
                                  <div className="flex flex-wrap gap-2">
                                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8"
                                      onClick={() => setAlert({ type: "mute_all", meetingId: meeting.id, title: meeting.title })}>
                                      <MicOff className="w-3.5 h-3.5 text-orange-500" /> {t("analytics.muteAll")}
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8"
                                      onClick={() => setAlert({ type: "lock", meetingId: meeting.id, title: meeting.title, locked: ms.locked })}>
                                      {ms.locked
                                        ? <><Unlock className="w-3.5 h-3.5 text-blue-500" /> {t("analytics.unlockMeeting")}</>
                                        : <><Lock className="w-3.5 h-3.5 text-blue-500" /> {t("analytics.lockMeeting")}</>}
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                      onClick={() => setAlert({ type: "end_meeting", meetingId: meeting.id, title: meeting.title })}>
                                      <PhoneOff className="w-3.5 h-3.5" /> {t("analytics.endMeeting")}
                                    </Button>
                                    <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
                                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                      <span className="font-mono text-xs text-foreground">{formatElapsed(meeting.entryTime)}</span>
                                      <span className="text-xs text-muted-foreground">{t("analytics.elapsed")}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      {ms.locked && <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0">{t("analytics.locked")}</Badge>}
                                    </div>
                                  </div>
                                </div>

                                {/* Per-participant controls */}
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("analytics.participants")}</p>
                                    <Badge variant="secondary" className="text-[10px]">{meeting.participants} {t("analytics.inMeeting")}</Badge>
                                  </div>
                                  <div className="space-y-1.5">
                                    {meeting.attendees.map((att) => {
                                      const isMuted = !!ms.mutedParticipants[att.name]
                                      const isVideoOff = !!ms.videoOffParticipants[att.name]
                                      return (
                                        <div key={att.name} className="flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-2.5 group">
                                          <Avatar name={att.name} size="sm" />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <p className="text-sm font-medium text-foreground truncate">{att.name}</p>
                                              {att.isHost && <Badge className="text-[9px] px-1.5 py-0 bg-primary/10 text-primary border-0">{t("analytics.host")}</Badge>}
                                              {isMuted && <Badge className="text-[9px] px-1.5 py-0 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 border-0">{t("analytics.muted")}</Badge>}
                                              {isVideoOff && <Badge className="text-[9px] px-1.5 py-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-0">{t("analytics.noVideo")}</Badge>}
                                            </div>
                                            <p className="text-[11px] text-muted-foreground truncate">{att.email}</p>
                                          </div>

                                          {/* Per-participant action buttons (hidden for host) - INCREASED SIZE */}
                                          {!att.isHost && (
                                            <div className="flex items-center gap-1">
                                              <button title={isMuted ? t("analytics.unmute") : t("analytics.mute")}
                                                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                                                onClick={() => setAlert({ type: "mute_participant", meetingId: meeting.id, participantName: att.name, muted: isMuted })}>
                                                {isMuted
                                                  ? <Volume2 className="w-4 h-4 text-emerald-500" />
                                                  : <VolumeX className="w-4 h-4 text-orange-500" />}
                                              </button>
                                              <button title={isVideoOff ? t("analytics.enableVideo") : t("analytics.disableVideo")}
                                                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                                                onClick={() => setAlert({ type: "disable_video", meetingId: meeting.id, participantName: att.name, videoOff: isVideoOff })}>
                                                <VideoOff className={`w-4 h-4 ${isVideoOff ? "text-emerald-500" : "text-slate-400"}`} />
                                              </button>
                                              <button title={t("analytics.removeFromMeeting")}
                                                className="w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center transition-colors"
                                                onClick={() => setAlert({ type: "remove_participant", meetingId: meeting.id, participantName: att.name })}>
                                                <UserX className="w-4 h-4 text-red-500" />
                                              </button>
                                            </div>
                                          )}
                                          {att.isHost && (
                                            <div className="flex items-center gap-1">
                                              <UserCheck className="w-4 h-4 text-primary" />
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>

                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
